/**
 * Public site shell (Reppr Elite, phase 7): sticky header + footer shared by
 * the landing page, pricing, the legal pages and the 404.
 *
 * Honesty deviations from the mock, deliberate: no "SOC 2 · GDPR · CCPA" strip
 * (not audited), no fake "all systems operational" indicator, no dead footer
 * links (About / Careers / Press / Changelog / System status don't exist).
 * Every link below goes somewhere real.
 */

import { type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { COMPANY_NAME, DEMO_BOOKING_URL, getSupportMailto } from '../../lib/config'
import styles from './PublicShell.module.css'

/**
 * `audience` decides the header. The agency pages carry the sales nav; the
 * page an agent sends to a family carries none of it — a parent deciding on
 * an agent has no use for our pricing, and the only door they need is their
 * own portal.
 */
export default function PublicShell({ children, audience = 'agencies' }: { children: ReactNode; audience?: 'agencies' | 'families' }) {
  const { pathname } = useLocation()
  const forFamilies = audience === 'families'

  return (
    <div className={styles.site}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.lockup}>
            <img src="/icon-512.png" alt="" width="26" height="26" />
            <span className={styles.wordmark}>Reppr</span>
          </Link>
          {forFamilies ? (
            <nav className={styles.nav} aria-label="Site">
              <Link to="/athletes" className={`${styles.navLink} ${styles.navActive}`}>For athletes and families</Link>
            </nav>
          ) : (
            <nav className={styles.nav} aria-label="Site">
              <a href="/#product" className={styles.navLink}>Product</a>
              <a href="/#why" className={styles.navLink}>Why Reppr</a>
              <a href="/#standards" className={styles.navLink}>Security</a>
              <Link
                to="/pricing"
                className={`${styles.navLink} ${pathname === '/pricing' ? styles.navActive : ''}`}
              >
                Pricing
              </Link>
            </nav>
          )}
          <div className={styles.headerRight}>
            {forFamilies ? (
              <>
                <Link to="/login" className={styles.navLink}>Agent? Sign in</Link>
                <Link to="/portal/login" className={styles.headerCta}>Your portal</Link>
              </>
            ) : (
              <>
                <Link to="/login" className={styles.navLink}>Sign in</Link>
                <Link to="/pricing" className={styles.headerCta}>Get started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={styles.main}>{children}</main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerGrid}>
            <div>
              <span className={styles.footerWordmark}>Reppr</span>
              <p className={styles.footerBlurb}>
                The desk for sports, PR and talent representation.
              </p>
            </div>
            {forFamilies ? (
              /* A family is not shopping. They get their door and the legal pages. */
              <div>
                <div className={styles.footerColLabel}>Your access</div>
                <nav className={styles.footerCol} aria-label="Access and legal">
                  <Link to="/portal/login" className={styles.footerLink}>Sign in to your portal</Link>
                  <a href={getSupportMailto()} className={styles.footerLink}>Contact support</a>
                  <Link to="/privacy" className={styles.footerLink}>Privacy policy</Link>
                  <Link to="/terms" className={styles.footerLink}>Terms of service</Link>
                </nav>
              </div>
            ) : (
              <>
                <div>
                  <div className={styles.footerColLabel}>Product</div>
                  <nav className={styles.footerCol} aria-label="Product">
                    <a href="/#product" className={styles.footerLink}>What it does</a>
                    <a href="/#why" className={styles.footerLink}>Client portal</a>
                    <Link to="/athletes" className={styles.footerLink}>For athletes and families</Link>
                    <a href="/#standards" className={styles.footerLink}>How we handle your data</a>
                  </nav>
                </div>
                <div>
                  <div className={styles.footerColLabel}>Resources</div>
                  <nav className={styles.footerCol} aria-label="Resources">
                    <Link to="/pricing" className={styles.footerLink}>Pricing</Link>
                    <a
                      href={DEMO_BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.footerLink}
                    >
                      {/* "Walkthrough" everywhere else; a second word for the
                          same thing reads like a second thing. */}
                      Book a walkthrough
                    </a>
                    <a href={getSupportMailto()} className={styles.footerLink}>Support</a>
                  </nav>
                </div>
                <div>
                  <div className={styles.footerColLabel}>Access</div>
                  <nav className={styles.footerCol} aria-label="Access and legal">
                    <Link to="/login" className={styles.footerLink}>Sign in</Link>
                    <Link to="/portal/login" className={styles.footerLink}>Client portal</Link>
                    <Link to="/terms" className={styles.footerLink}>Terms of service</Link>
                    <Link to="/privacy" className={styles.footerLink}>Privacy policy</Link>
                  </nav>
                </div>
              </>
            )}
          </div>
          <div className={styles.footerBottom}>
            <span className={styles.footerLegal}>
              © {new Date().getFullYear()} REPPR · {COMPANY_NAME.toUpperCase()}
            </span>
          </div>
        </div>
      </footer>
    </div>
  )
}
